import React, { useMemo } from 'react'
import styled from 'styled-components'

import { Tooltip } from '@blade/ui-kit'
import { usePairAgeFormatted } from '@root/entities/pair'
import { PlatformUtil } from '@root/shared'
import { useDateFormat } from '@root/shared/localization'
import { SolScanIcon } from '@root/shared/ui-kit'

import { Network } from '../../model'

import type { TxHash } from '@root/entities/transaction'
import type { ClassNameProps, StyleProps } from '@root/shared/react'

type TradeAgeCellProps = {
  timestamp: Date
  transactionHash: TxHash | undefined
  network: Network.Network
} & ClassNameProps &
  StyleProps

export const TransactionAgeCell: React.FC<TradeAgeCellProps> = React.memo(
  ({ className, timestamp, transactionHash, network, style }) => {
    const { formatted: tradeAge } = usePairAgeFormatted(timestamp)
    const { formatDate } = useDateFormat()

    const link = useMemo(() => {
      if (!transactionHash) {
        return undefined
      }
      return Network.makeExplorerTransactionLink(network, transactionHash)
    }, [network, transactionHash])

    const formattedDate = useMemo(() => {
      return formatDate(timestamp, 'PPpp')
    }, [formatDate, timestamp])

    return (
      <Tooltip content={formattedDate}>
        <Root className={className} style={style}>
          {link ? (
            <Link
              onClick={() => {
                PlatformUtil.openLink(link)
              }}
            >
              <SolScanIcon size={14} />
            </Link>
          ) : undefined}
          <Label>{tradeAge}</Label>
        </Root>
      </Tooltip>
    )
  },
)

const Root = styled.div`
  display: grid;
  gap: 6px;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
`

const Link = styled.div`
  color: var(--text-11);

  display: grid;
  align-items: center;
  justify-items: center;
  cursor: pointer;

  transition: color 0.15s linear;

  &:hover {
    color: var(--text-12);
  }

  &:visited {
    color: var(--appearance-inv-1);
  }
`

const Label = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--row-color);
`
